import React, { useState } from 'react';
import { useTheme } from '../ThemeContext';
import useBreakpoint from '../hooks/useBreakpoint';

const FEATURES = [
  { icon: 'receipt_long', label: 'Spending Log', desc: 'Log actual transactions and compare spending against your budget by category' },
  { icon: 'trending_up', label: 'Extended Projections', desc: 'Forecast 3, 6, or 12 months ahead with optimistic and conservative confidence bands' },
  { icon: 'calendar_month', label: 'Extended Calendar & Forecast', desc: 'Navigate up to 12 months forward in Calendar and Forecast views' },
  { icon: 'calculate', label: 'Debt Payoff Calculator', desc: 'Compare avalanche vs. snowball strategies — see payoff dates and interest totals' },
  { icon: 'notifications', label: 'Smart Alerts', desc: 'Email reminders before bills are due, low balance warnings, and monthly summaries' },
  { icon: 'folder', label: 'Multiple Budget Trackers', desc: 'Manage separate budgets — personal, business, household — from one account' },
  { icon: 'download', label: 'Data Export', desc: 'Export your full financial data as JSON or CSV at any time' },
];

export default function UpgradeModal({ authUser, onClose, onProActivated }) {
  const { t } = useTheme();
  const bp = useBreakpoint();
  const isMobile = bp === 'mobile';
  const isTablet = bp === 'tablet';
  const isDesktop = bp === 'desktop';

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleUpgrade = async () => {
    setLoading(true);
    setError('');
    try {
      if (!authUser) {
        alert('Please sign in to subscribe.');
        setLoading(false);
        return;
      }
      const res = await fetch('/api/create-checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ uid: authUser.uid, email: authUser.email }),
      });
      if (!res.ok) throw new Error('Could not create checkout session');
      const { url } = await res.json();
      window.location.href = url;
    } catch (err) {
      setError('Payment setup unavailable. Please try again later.');
      setLoading(false);
    }
  };

  // Responsive card padding
  const cardPadding = isDesktop ? '36px 32px' : isTablet ? '28px 24px' : '22px 18px';
  const featureGap = isDesktop ? '14px' : isTablet ? '12px' : '10px';

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, zIndex: 1000,
      background: t.overlay,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '16px',
    }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        background: t.card, borderRadius: '16px', padding: cardPadding,
        maxWidth: '460px', width: '100%',
        boxShadow: t.shadowXL,
      }}>
        <div style={{ textAlign: 'center', marginBottom: '28px' }}>
          <div style={{
            display: 'inline-block', fontSize: '13px', fontWeight: '700',
            padding: '5px 14px', background: 'linear-gradient(135deg, #6a11cb, #2575fc)',
            color: 'white', borderRadius: '20px', letterSpacing: '0.04em', marginBottom: '14px',
          }}>PRO</div>
          <h2 style={{ margin: '0 0 8px', fontSize: '24px', fontWeight: '700', color: t.text }}>
            Upgrade to Budget Planner Pro
          </h2>
          <p style={{ margin: 0, color: t.textMuted, fontSize: '14px', lineHeight: '1.5' }}>
            Everything you need to take full control of your finances.
          </p>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: featureGap, marginBottom: '28px' }}>
          {FEATURES.map((f, i) => (
            <div key={i} style={{ display: 'flex', gap: '14px', alignItems: 'flex-start' }}>
              <span className="material-symbol" style={{ flexShrink: 0, marginTop: '1px' }}>{f.icon}</span>
              <div>
                <div style={{ fontWeight: '600', fontSize: '14px', color: t.text, marginBottom: '2px' }}>{f.label}</div>
                <div style={{ fontSize: '13px', color: t.textMuted, lineHeight: '1.4' }}>{f.desc}</div>
              </div>
            </div>
          ))}
        </div>

        <div style={{
          background: t.pageBg, borderRadius: '10px', padding: '16px 20px',
          textAlign: 'center', marginBottom: '20px',
        }}>
          <div style={{ fontSize: '28px', fontWeight: '700', color: t.text }}>
            $5.99 <span style={{ fontSize: '16px', fontWeight: '400', color: t.textMuted }}>/ month</span>
          </div>
          <div style={{ fontSize: '13px', color: t.textMuted, marginTop: '4px' }}>or $49.99/year · Cancel any time</div>
        </div>

        {error && (
          <div style={{ background: '#fce4ec', color: '#ba1a1a', borderRadius: '8px', padding: '10px 14px', fontSize: '13px', marginBottom: '12px' }}>
            {error}
          </div>
        )}

        <button onClick={handleUpgrade} disabled={loading} style={{
          width: '100%', padding: '14px',
          background: loading ? '#ccc' : 'linear-gradient(135deg, #6a11cb, #2575fc)',
          color: 'white', border: 'none', borderRadius: '10px',
          fontSize: '15px', fontWeight: '700', cursor: loading ? 'not-allowed' : 'pointer',
          fontFamily: 'inherit', marginBottom: '10px',
        }}>
          {loading ? 'Redirecting…' : 'Get Pro — $5.99/mo'}
        </button>

        <button onClick={onClose} style={{
          width: '100%', padding: '10px', background: 'transparent',
          color: t.textFaint, border: 'none', cursor: 'pointer', fontSize: '13px', fontFamily: 'inherit',
        }}>
          Maybe later
        </button>
      </div>
    </div>
  );
}
